import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  OnInit,
  HostListener,
  viewChildren,
  DestroyRef,
  inject,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BHASHINI_SCRIPT,
  BHASHINI_LOGO_URL,
  BHASHINI_FEEDBACK_ICON_URL,
} from '../../../constants/Bhashini_script';
import { ToasterService } from '../../../services/toaster.service';
import { LANGUAGES } from '../../../constants/language.constants';
import { encryptData } from '../../../constants/enc-dec.helper';
import { cachingKeys } from '../../../constants/caching-keys';

import {
  LanguageFeedbackComponent,
  LanguageFeedbackModel,
} from '../language-feedback/language-feedback.component';

import { ApiService } from '../../../services/api.service';
import { LoggedInUser } from '../../../models/logged-in-user.model';
import { CachingService } from '../../../services/caching.service';
import { take, tap, Subscription } from 'rxjs';
@Component({
  selector: 'app-bhashini-language-translation',
  standalone: true,
  imports: [FormsModule, LanguageFeedbackComponent],
  templateUrl: './bhashini-language-translation.component.html',
  styleUrl: './bhashini-language-translation.component.scss',
})
export class BhashiniLanguageTranslationComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('languageFeedback')
  languageFeedback!: LanguageFeedbackComponent;
  @ViewChild('languageWidthMeasure')
  languageWidthMeasure!: ElementRef<HTMLElement>;
  @ViewChild('languageSearchInput')
  languageSearchInput?: ElementRef<HTMLInputElement>;
  languageOptions =
    viewChildren<ElementRef<HTMLButtonElement>>('languageOption');

  highlightedLanguageIndex = 0;

  private languageErrorShown = false;
  isFeedbackModalOpen = false;
  languages = LANGUAGES;
  readonly BHASHINI_LOGO_URL = BHASHINI_LOGO_URL;
  readonly BHASHINI_FEEDBACK_ICON_URL = BHASHINI_FEEDBACK_ICON_URL;
  readonly loaderLines = Array(12);
  isLanguageDropdownOpen = false;
  userProfile: LoggedInUser['user'];
  private userToken: any;
  isBhashiniLoading = true;
  isAuthResolved = false;
  selectedLanguage = 'en';

  private readonly destroyRef = inject(DestroyRef);

  selectedLanguageName = 'English';
  languageSearchText = '';
  dropdownWidth = 130;

  private readonly LANGUAGE_DROPDOWN_HORIZONTAL_PADDING = 90;

  private readonly LANGUAGE_DROPDOWN_MIN_WIDTH = 130;

  private readonly LANGUAGE_DROPDOWN_MAX_WIDTH = 260;
  private routerSubscription?: Subscription;
  private originalConsoleLog?: typeof console.log;
  private skipTranslationObserver: MutationObserver | null = null;
  constructor(
    private toasterService: ToasterService,
    private apiService: ApiService,
    private cachingService: CachingService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }
  get currentLanguage(): string {
    return this.cachingService.getPreferredLanguage();
  }

  set currentLanguage(value: string) {
    this.selectedLanguage = value;
    this.cachingService.updatePreferredLanguage(value);

    if (this.userProfile?.id) {
      const profileDetails = {
        ...this.userProfile,
        preferredLanguage: value,
      };

      const currentState = this.cachingService.loggedInUser.value;

      const updatedState: LoggedInUser = {
        ...currentState,
        user: profileDetails,
      };

      localStorage.setItem(cachingKeys.loggedInUser, encryptData(updatedState));

      this.cachingService.loggedInUser.next(updatedState);
    }
  }
  ngOnInit(): void {
    this.getUserProfile();
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isLanguageDropdownOpen = false;
      }
    });
  }
  getUserProfile() {
    const loggedInUser = this.cachingService.fetchUserInfo();

    if (loggedInUser) {
      this.cachingService?.loggedInUser.next(loggedInUser);
      this.cachingService?.loggedInUser
        .pipe(
          take(1),
          tap(({ token }) => {
            this.userToken = token;
          }),
        )
        .subscribe();
    }

    this.cachingService?.loggedInUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const user = data?.user;
        this.userProfile = user;
        this.cachingService.userId = user?.id;
        this.isAuthResolved = true;
      });
  }
  ngAfterViewInit(): void {
    const preferredLanguage = this.cachingService.getPreferredLanguage();
    this.selectedLanguage = preferredLanguage;

    setTimeout(() => {
      const language = this.languages.find(
        (item) => item.code === preferredLanguage,
      );
      this.selectedLanguageName = language?.name || 'English';
      this.updateDropdownWidth(preferredLanguage);
    });

    if (isPlatformBrowser(this.platformId)) {
      this.addBhashiniSkipToElements();
      this.loadBhashiniScript();
    }
  }
  private loadBhashiniScript(): void {
    const script = document.createElement('script');

    script.src = BHASHINI_SCRIPT;
    script.setAttribute('isReload', 'false');
    script.async = true;

    script.onerror = () => {
      this.handleLanguageServiceFailure();
    };

    script.onload = () => {
      this.hideBhashiniControls();
      this.applyPreferredLanguageOnLoad();
    };
    this.originalConsoleLog = console.log;

    console.log = (...args: unknown[]) => {
      if (typeof args[0] === 'string' && args[0].startsWith('[Bhashini]')) {
        return;
      }
      this.originalConsoleLog?.apply(console, args);
    };

    document.body.appendChild(script);
    setTimeout(() => {
      this.isBhashiniLoading = false;
    }, 2000);
  }

  selectLanguage(language: { code: string; name: string }): void {
    this.currentLanguage = language.code;
    this.selectedLanguageName = language.name;
    this.isLanguageDropdownOpen = false;
    this.onLanguageChange(language.code);
  }

  toggleLanguageDropdown(): void {
    if (this.isBhashiniLoading) {
      return;
    }

    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;

    if (!this.isLanguageDropdownOpen) {
      this.languageSearchText = '';
      return;
    }

    this.highlightedLanguageIndex = this.filteredLanguages.findIndex(
      (language: any) => language.code === this.currentLanguage,
    );

    if (this.highlightedLanguageIndex === -1) {
      this.highlightedLanguageIndex = 0;
    }

    setTimeout(() => {
      this.languageSearchInput?.nativeElement.focus();
    });
  }

  onLanguageDropdownKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (this.isBhashiniLoading) {
      return;
    }

    const filteredLanguages = this.filteredLanguages;
    const filteredLength = filteredLanguages.length;

    if (!filteredLength) {
      return;
    }

    switch (keyboardEvent.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        keyboardEvent.preventDefault();
        this.isLanguageDropdownOpen = true;

        this.highlightedLanguageIndex = filteredLanguages.findIndex(
          (language: any) => language.code === this.currentLanguage,
        );

        if (this.highlightedLanguageIndex === -1) {
          this.highlightedLanguageIndex = 0;
        }

        if (keyboardEvent.key === 'ArrowDown') {
          this.highlightedLanguageIndex =
            (this.highlightedLanguageIndex + 1) % filteredLength;
        }

        if (keyboardEvent.key === 'ArrowUp') {
          this.highlightedLanguageIndex =
            (this.highlightedLanguageIndex - 1 + filteredLength) %
            filteredLength;
        }

        setTimeout(() => {
          this.languageOptions()?.[
            this.highlightedLanguageIndex
          ]?.nativeElement.focus();
        });
        break;

      case 'Escape':
        keyboardEvent.preventDefault();
        this.isLanguageDropdownOpen = false;
        this.languageSearchText = '';
        break;
    }
  }

  onLanguageSearchKeydown(event: KeyboardEvent): void {
    const filteredLanguages = this.filteredLanguages;
    const filteredLength = filteredLanguages.length;

    switch (event.key) {
      case 'ArrowDown':
        if (!filteredLength) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.highlightedLanguageIndex = 0;
        setTimeout(() => {
          this.languageOptions()?.[0]?.nativeElement.focus();
        });
        break;

      case 'ArrowUp':
        if (!filteredLength) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.highlightedLanguageIndex = filteredLength - 1;
        setTimeout(() => {
          this.languageOptions()?.[
            this.highlightedLanguageIndex
          ]?.nativeElement.focus();
        });
        break;

      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.isLanguageDropdownOpen = false;
        this.languageSearchText = '';
        break;
    }
  }

  onLanguageOptionKeydown(event: KeyboardEvent, index: number): void {
    const filteredLanguages = this.filteredLanguages;
    const filteredLength = filteredLanguages.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedLanguageIndex = (index + 1) % filteredLength;
        this.languageOptions()?.[
          this.highlightedLanguageIndex
        ]?.nativeElement.focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedLanguageIndex =
          (index - 1 + filteredLength) % filteredLength;
        this.languageOptions()?.[
          this.highlightedLanguageIndex
        ]?.nativeElement.focus();
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectLanguage(filteredLanguages[index]);
        break;

      case 'Escape':
        event.preventDefault();
        this.isLanguageDropdownOpen = false;
        this.languageSearchText = '';
        (event.target as HTMLButtonElement).blur();
        break;
    }
  }

  onLanguageChange(languageCode: string): void {
    this.isBhashiniLoading = true;

    this.currentLanguage = languageCode;
    this.updateDropdownWidth(languageCode);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      location.reload();
    }
  }

  private applyPreferredLanguageOnLoad(): void {
    const preferredLanguage = this.cachingService.getPreferredLanguage();

    if (!preferredLanguage || preferredLanguage === 'en') {
      return;
    }

    setTimeout(() => {
      const option = this.getLanguageOption(preferredLanguage);
      option?.click();
    }, 0);
  }

  private updateDropdownWidth(languageCode: string): void {
    const selectedLanguage = this.languages.find(
      (language) => language.code === languageCode,
    );

    this.selectedLanguageName = selectedLanguage?.name || 'English';

    setTimeout(() => {
      if (!this.languageWidthMeasure || !isPlatformBrowser(this.platformId)) {
        return;
      }

      const textWidth =
        this.languageWidthMeasure.nativeElement.getBoundingClientRect().width;

      const calculatedWidth = Math.ceil(
        textWidth + this.LANGUAGE_DROPDOWN_HORIZONTAL_PADDING,
      );

      this.dropdownWidth = Math.min(
        this.LANGUAGE_DROPDOWN_MAX_WIDTH,
        Math.max(this.LANGUAGE_DROPDOWN_MIN_WIDTH, calculatedWidth),
      );
    });
  }

  private getLanguageOption(languageCode: string): HTMLElement | null {
    return document.querySelector(
      `.language-option[data-value="${languageCode}"]`,
    ) as HTMLElement | null;
  }

  private hideBhashiniControls(): void {
    const bhashiniBtn = document.querySelector(
      '.bhashini-dropdown-btn',
    ) as HTMLElement | null;

    const bhashiniDropdown = document.querySelector(
      '#bhashiniLanguageDropdown',
    ) as HTMLElement | null;

    bhashiniBtn?.style.setProperty('display', 'none');
    bhashiniDropdown?.style.setProperty('display', 'none');
    this.languageSearchText = '';
  }

  get filteredLanguages() {
    const search = this.languageSearchText.trim().toLowerCase();

    if (!search) {
      return this.languages;
    }

    return this.languages.filter((language) =>
      language.name.toLowerCase().includes(search),
    );
  }

  openBhashiniFeedback(): void {
    const feedbackButton = document.querySelector(
      '#bhashini-translation .bhashini-feedback-button',
    ) as HTMLElement | null;

    if (feedbackButton) {
      feedbackButton.click();
    }
  }

  private handleLanguageServiceFailure(): void {
    this.resetToEnglish();
    this.hideBhashiniDropdown();
    this.showLanguageServiceError();
  }

  private hideBhashiniDropdown(): void {
    const widget = document.querySelector(
      '.bhashini-dropdown-content',
    ) as HTMLElement | null;

    widget?.style.setProperty('display', 'none');
  }

  private resetToEnglish(): void {
    this.currentLanguage = 'en';
    this.selectedLanguageName = 'English';
    this.updateDropdownWidth('en');

    const languageDropdown = document.querySelector(
      '.bhashini-native-select',
    ) as HTMLSelectElement | null;

    if (!languageDropdown) {
      return;
    }

    languageDropdown.value = 'en';
    languageDropdown.dispatchEvent(
      new Event('change', {
        bubbles: true,
      }),
    );
  }

  private showLanguageServiceError(): void {
    if (this.languageErrorShown) {
      return;
    }

    this.languageErrorShown = true;

    this.toasterService.error(
      'Translation service is currently unavailable. The application will continue in English.',
    );

    setTimeout(() => {
      this.languageErrorShown = false;
    }, 5000);
  }

  openLanguageFeedback(): void {
    this.languageFeedback.open();
  }

  onLanguageFeedbackSubmitted(feedback: LanguageFeedbackModel): void {
    const formData = {
      userid: this.userProfile?.id || null,
      language: this.currentLanguage,
      ...feedback,
    };

    this.apiService.languageFeedback(formData).subscribe({
      next: (res: any) => {
        if (res?.header?.error || res?.header?.success === false) {
          this.toasterService.error(
            'Unable to submit feedback. Please try again.',
          );
          return;
        }

        this.toasterService.success('Feedback submitted successfully!');
      },
      error: () => {
        this.toasterService.error(
          'Unable to submit feedback. Please try again.',
        );
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      this.isLanguageDropdownOpen &&
      !target.closest('.bhashini-plugin-container')
    ) {
      this.isLanguageDropdownOpen = false;
    }
    this.languageSearchText = '';
  }

  private addBhashiniSkipToElements(): void {
    this.markSkipTranslationElements(document.body);

    this.skipTranslationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') {
          continue;
        }
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.markSkipTranslationElements(node as Element);
          }
        });
      }
    });

    this.skipTranslationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private markSkipTranslationElements(root: Element): void {
    const selector =
      'mat-icon:not(.bhashini-skip-translation), img:not(.bhashini-skip-translation), i:not(.bhashini-skip-translation)';

    if (root.matches?.(selector)) {
      root.classList.add('bhashini-skip-translation');
    }

    root.querySelectorAll(selector).forEach((el) => {
      el.classList.add('bhashini-skip-translation');
    });
  }

  ngOnDestroy(): void {
    if (this.originalConsoleLog) {
      console.log = this.originalConsoleLog;
    }
    this.skipTranslationObserver?.disconnect();
    this.skipTranslationObserver = null;
  }
}
